declare global {
  interface Window {
    yatta: CheckoutDropInApi;
  }

  const yatta: CheckoutDropInApi;
}

/**
 * Definition of all Yatta Checkout JavaScript drop-in API functions (see docs at https://yatta.de/documentation/integrating-yatta-checkout-web).
 * Version: 08 March 2024
 */
export interface CheckoutDropInApi {
  /**
   * Start the Checkout for a given product (see docs at https://yatta.de/documentation/integrating-yatta-checkout-web#initialization-and-mandatory-event-handler) for:
   * - a one-time purchase or subscription (together “purchase”)
   * - the respective purchase information including the subscription management where applicable, if already purchased
   * @param productId The id of the product checkout shall be started for.
   * @param params Optional params for starting checkout.
   */
  startCheckout(productId: string, params?: CheckoutStartParams | null): void;

  /**
   * Allows the user to sign in or register, then closes without taking further actions (see docs at https://yatta.de/documentation/integrating-yatta-checkout-web#standalone-sign-in).
   * @param vendorId The id of the vendor the sign-in shall be started for.
   * @param params Optional params for starting sign-in.
   */
  signIn(vendorId: string, params?: GeneralStartParams | null): void;

  /**
   * Silently signs out the user (see docs at https://yatta.de/documentation/integrating-yatta-checkout-web#sign-out-and-close).
   */
  signOut(): void;

  /**
   * Links a non-Yatta account to the checkout drop-in to assure the access to licenses for products which require a link to this account.
   * If a product which required such an account link is sold via the checkout drop-in, this API must be called everytime the user signs in or out with the corresponding account and before the `DOMContentLoaded` browser event.
   * Otherwise the checkout will show an error page and the autostart of the checkout from email links will not work properly.
   * @param authenticationParams The id and email of the linked account. Set to a value, if the user is signed in, otherwise to null.
   */
  linkAccount(linkedAccount?: LinkedAccount | null): void;

  /**
   * Register for changes to the signed-in boolean of the current user (see docs at https://yatta.de/documentation/integrating-yatta-checkout-web#log-changes).
   * @param callback Callback to be executed when the signedIn boolean indicator of the user changes.
   * @param options Optional options for the callback.
   */
  onSignedInChange(callback: SignedInChangeCallback, options?: CheckoutCallbackOptions | null): void;

  /**
   * Register for changes to the asset status of the current user for a given product (see docs at https://yatta.de/documentation/integrating-yatta-checkout-web#log-changes).
   *
   * @param productId The id of the product the asset status shall be retrieved for.
   * @param callback Callback to be executed when the asset status of the user changes.
   * @param options Optional options for the callback.
   */
  onAssetStatusChange(productId: string, callback: AssetStatusChangeCallback, options?: AssetStatusChangeCallbackOptions | null): void;

  /**
   * Register for changes to the session token, which can be used in conjunction with the API Key to get information for any vendor product for the current user session (see docs at https://yatta.de/documentation/integrating-yatta-checkout-web#log-changes).
   *
   * @param vendorId The id of the vendor requesting the user's session.
   * @param callback Callback to be executed when the session token of the user changes.
   * @param options Optional options for the callback.
   */
  onSessionChange(vendorId: string, callback: SessionChangeCallback, options?: CheckoutCallbackOptions | null): void;

  /**
   * ATTENTION: ONLY FOR YATTA INTERNAL USE!
   * Start an academic registration.
   * @param params Optional params for starting the academic registration.
   */
  startAcademicRegistration(params?: AcademicRegistrationStartParams | null): void;
}

/**
 * The optional parameters which can be provided when starting checkout by the 'startCheckout' drop-in interface.
 */
export interface CheckoutStartParams extends GeneralStartParams {
  /**
   * Can be set to 'PREVIEW', if the preview of the product shall be loaded for testing purposes.
   * By default set to 'LIVE'.
   */
  environment?: CheckoutEnvironment | null;

  /**
   * Object which can be set, if the user shall manage their contract instead of purchasing.
   * By default set to null, which means the user will purchase the product or manage it, if they purchased already and an additional purchase is not allowed.
   */
  manageContract?: ManagedContract | null;

  /**
   * Can be set to define, if a one-time purchase or a subscription shall be pre-selected.
   * By default set to one-time purchase mode.
   */
  start?: OneTimePurchaseMode | SubscriptionMode | null;
}

export interface ManagedContract {
  /**
   * Can be set to manage a specific contract.
   * By default set to null, which means the contract with last payment will be managed
   */
  contractId?: string | null;
}

/**
 * The optional start mode contained in the 'CheckoutStartParams' interface for a one-time purchase.
 */
export interface OneTimePurchaseMode {
  /**
   * Identifies the checkout start pre-selection to be a one-time purchase.
   */
  type?: 'ONE_TIME_PURCHASE' | null;

  /**
   * Identifies the term of the one-time purchase.
   */
  term?: Term | null;

  /**
   * Can be set to define the quantity of the product to be purchased or subscribed.
   * By default set to the minimum possible value.
   */
  quantity?: number | null;
}

/**
 * The optional start mode contained in the 'CheckoutStartParams' interface for a subscription.
 */
export interface SubscriptionMode {
  /**
   * Identifies the checkout start pre-selection to be a subscription.
   */
  type: 'SUBSCRIPTION';

  /**
   * Can be set to define the pre-selected subscription interval.
   * By default set to 'MONTHLY'.
   */
  subscriptionInterval?: SubscriptionInterval | null;
}

/**
 * Meta data to be persisted with the purchase and included in the purchase callback
 */
export interface Metadata {
  /**
   * Custom meta data to be persisted with the purchase and included in the purchase callback
   * A JSON object with key-value pairs of strings, numbers, booleans, arrays or nested objects
   * Max. 64 keys, max. 42 characters per key, max. 1024 characters per value
   */
  customMetadata?: CustomMetadata | null;

  /**
   * On success handling for the drop-in flow
   */
  onSuccess?: RedirectHandling | null;

  /**
   * On close handling for the drop-in flow
   */
  onClose?: RedirectHandling | null;

  /************************************************
   * ONLY FOR BACKWARD COMPATIBILITY:
   * billingInformation, groupContext
   ***********************************************/
  /**
   * Billing information which are persisted at the user and preselected for a purchase
   */
  billingInformation?: BillingInformation | null;

  /**
   * Group context for the user
   */
  groupContext?: GroupContext | null;
}

/**
 * Billing information which are preselected for the purchase
 */
export interface BillingInformation {
  /**
   * First name of the person
   */
  firstName?: string | null;

  /**
   * Last name of the person
   */
  lastName?: string | null;

  /**
   * Name of the organization
   */
  organizationName?: string | null;

  /**
   * First line of address (street and house number)
   */
  address?: string | null;

  /**
   * Second line of address
   */
  address2?: string | null;

  /**
   * Postal code of address
   */
  postalCode?: string | null;

  /**
   * City of address
   */
  city?: string | null;

  /**
   * Optional province
   */
  province?: string | null;

  /**
   * Country
   */
  country?: string | null;

  /**
   * Billing email address
   */
  email?: string | null;

  /**
   * VAT ID
   */
  vatId?: string | null;
}

/**
 * A redirection
 */
export interface RedirectHandling {
  /**
   * The url to redirect to
   */
  redirectUrl?: string | null;
}

/**
 * Group context for the purchase
 */
export interface GroupContext {
  /**
   * Identifier of the group
   */
  id: string;

  /**
   * Name of the group
   */
  name: string;
}

/**
 * Custom meta data to be persisted with the purchase and included in the purchase callback
 */
export type CustomMetadata = Record<string, any>;

/**
 * The email and id of the vendor account which shall be linked to the Yatta user account.
 */
export interface LinkedAccount {
  /**
   * Account id of the vendor user account to be linked
   */
  accountId: string;

  /**
   * Email address of the vendor user account to be linked
   */
  email?: string | null;
}

/**
 * Environments a product can be loaded for in checkout.
 * Live: Purchase the product with real payment for real usage right.
 * Preview: A preview (demo) checkout of your product for testing purposes.
 */
export type CheckoutEnvironment = 'LIVE' | 'PREVIEW';

/**
 * Type of callback, if the user's signed-in status changes
 */
export type SignedInChangeCallback = CheckoutCallback<boolean>;

/**
 * Type of callback, if the user's asset status for a certain product changes
 */
export type AssetStatusChangeCallback = CheckoutCallback<AssetStatus>;

/**
 * Type of callback, if the user's session changes
 */
export type SessionChangeCallback = CheckoutCallback<CheckoutSession | null>;

/**
 * Type of callback to be executed with a value parameter of type T
 */
export type CheckoutCallback<T> =
  /**
   * @param value Value passed to the callback
   */
  (value: T) => void;

/**
 * The options for the checkout callbacks
 */
export interface CheckoutCallbackOptions {
  /**
   * Enable async execution of the callback, i.e. while the checkout is still open.
   */
  asyncExecution?: boolean | null;
}

/**
 * The asset status for a certain product provided to the callback in the 'onAssetStatusChange' drop-in interface.
 */
export interface AssetStatus {
  /**
   * Array of status depending on the payment type connected with the assets.
   */
  status: DeprecatedSingleAssetStatus[];

  /**
   * Array of status depending on the payment type connected with the assets.
   */
  assetStatus: SingleAssetStatus[];
}

/**
 * Status of a single asset.
 */
export type SingleAssetStatus = OneTimePurchaseStatus | SubscriptionStatus;

export interface AssetStatusChangeCallbackOptions extends CheckoutCallbackOptions {
  /**
   * Environment, which can be set to 'PREVIEW', if the asset status shall be retrieved for preview of the product for testing purposes.
   */
  environment?: CheckoutEnvironment | null;
}

export interface AssetStatusEssentials {
  /**
   * The contract id of the asset.
   */
  contractId: string;
}

/**
 * The one-time purchase status contained in the AssetStatus interface.
 */
export interface OneTimePurchaseStatus extends AssetStatusEssentials {
  /**
   * Identifies the asset to be of a one-time purchase type.
   */
  type: 'ONE_TIME_PURCHASE';

  /**
   * Booking status of the one-time purchase.
   */
  bookingStatus: OneTimePurchaseBookingStatus;
}

/**
 * A term a user buys for.
 */
export type Term = LimitedTerm | 'UNLIMITED';

/**
 * A limited term a user buys for.
 */
export interface LimitedTerm extends LimitedTermDuration {
  /**
   * The optional start of the term, specified in ISO-8601 format, e.g. 2019-09-26T00:00:00+0200.
   */
  startOfTerm?: string | null;
}

/**
 * The duration of the limited term a user buys for.
 */
export interface LimitedTermDuration {
  /**
   * The runtime the user buys.
   */
  runtime: number;

  /**
   * The unit of the limited term.
   */
  unit: LimitedTermUnit;
}

/**
 * Unit of a limited term.
 */
export type LimitedTermUnit = 'MINUTE' | 'HOUR' | 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';

/**
 * The subscription status contained in the AssetStatus interface.
 */
export interface SubscriptionStatus extends AssetStatusEssentials {
  /**
   * Identifies the asset status to be of a subscription type.
   */
  type: 'SUBSCRIPTION';

  /**
   * Booking status of the subscription.
   */
  bookingStatus: SubscriptionBookingStatus;
}

/**
 * Booking status of a subscription.
 */
export type SubscriptionBookingStatus = 'BOOKED' | 'EXPIRING' | 'SUBSCRIBED';

/**
 * Booking status of a subscription.
 */
export type OneTimePurchaseBookingStatus = 'BOOKED' | 'PURCHASED';

/**
 * Interval a user subscribes for.
 */
export type SubscriptionInterval = 'MONTHLY' | 'QUARTERLY' | 'YEARLY';

/**
 * Session, which can be used in conjunction with the API Key to get information for any vendor product for the current user.
 */
export interface CheckoutSession {
  /**
   * Account id (either autogenerated or linked account id) of the currently signed in user
   */
  accountId: string;

  /**
   * Session token to be used in conjunction with the API Key to get information for any vendor product for the current user.
   */
  sessionToken: string;
}

export interface AcademicRegistrationStartParams extends GeneralStartParams {
  /**
   * The optional id of the product the academic registration shall be started for.
   */
  productId?: string | null;

  /**
   * The optional pre-selection of the academic registration type. By default set to StudentMode.
   */
  start?: StudentMode | TeacherMode | OrganizationMode | null;
}

/**
 * The optional start mode contained in the 'AcademicRegistrationStartParams' interface for a registration as a student.
 */
export interface StudentMode {
  /**
   * Identifies the academic registration start pre-selection to be for a student.
   */
  type?: 'STUDENT' | null;
}

/**
 * The optional start mode contained in the 'AcademicRegistrationStartParams' interface for a registration as a teacher.
 */
export interface TeacherMode {
  /**
   * Identifies the academic registration start pre-selection to be for a teacher.
   */
  type: 'TEACHER';
}

/**
 * The optional start mode contained in the 'AcademicRegistrationStartParams' interface for a registration as an organization.
 */
export interface OrganizationMode {
  /**
   * Identifies the academic registration start pre-selection to be for a organization.
   */
  type: 'ORGANIZATION';
}

/**
 * The general parameters for all drop-in interfaces which open the drop-in.
 */
export interface GeneralStartParams {
  /**
   * Boolean, which can be set to true, if the user should be first signed out before performing the action.
   * By default set to false.
   */
  forceSignIn?: boolean | null;

  /**
   * Can be set to override 'en-US' as default language, but not the user's personally saved language settings.
   */
  locale?: CheckoutLocale | null;

  /**
   * Id of metadata which was previously persisted via backend-API and is to be persisted with a purchase and to be included in the purchase callback.
   * Overrules the metadata field.
   */
  metadataId?: string | null;

  /**
   * Meta data to be persisted when opening a drop-in and with a purchase and included in the purchase callback.
   * Is ignored if metadataId is set.
   */
  metadata?: Metadata | null;
}

/**
 * Locales the checkout can be started for
 */
export type CheckoutLocale = CheckoutLanguage | 'de-DE' | 'en-US';

export type CheckoutLanguage = 'de' | 'en';

/**************************
 * DEPRECATED API
 *************************/

/**
 * Status of a single asset.
 */
export type DeprecatedSingleAssetStatus = OneTimePurchaseStatus | DeprecatedSubscriptionStatus;

/**
 * The subscription status contained in the AssetStatus interface.
 */
export interface DeprecatedSubscriptionStatus extends AssetStatusEssentials {
  /**
   * Identifies the asset status to be of a subscription type.
   */
  type: 'SUBSCRIPTION';

  /**
   * Booking status of the subscription.
   */
  bookingStatus: DeprecatedSubscriptionBookingStatus;
}

export type DeprecatedSubscriptionBookingStatus = 'ONGOING' | 'CANCELED';
